import { API_BASE_URL } from "../config";

const BASE = "/api/support-requests";

const getAuthHeaders = () => {
  const user = JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}");
  return user.token ? { Authorization: `Bearer ${user.token}` } : {};
};

const handleResponse = async (response, errorMsg) => {
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || errorMsg);
  }
  return response.json();
};

export const supportRequestApi = {
  async create(data) {
    const formData = new FormData();
    formData.append("category", data.category);
    formData.append("subject", data.subject);
    formData.append("description", data.description);
    if (data.stepsToReproduce) {
      formData.append("steps_to_reproduce", data.stepsToReproduce);
    }
    if (data.pageUrl) {
      formData.append("page_url", data.pageUrl);
    }
    if (data.browserInfo) {
      formData.append("browser_info", data.browserInfo);
    }
    if (data.attachments && data.attachments.length > 0) {
      data.attachments.forEach((file) => {
        formData.append("attachments", file);
      });
    }

    const response = await fetch(`${API_BASE_URL}${BASE}/`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: formData,
    });
    return handleResponse(response, "Failed to submit request");
  },

  async getMyRequests(status = null, limit = 50) {
    const params = new URLSearchParams();
    if (status) {
      params.append("status", status);
    }
    params.append("limit", String(limit));

    const response = await fetch(`${API_BASE_URL}${BASE}/?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response, "Failed to fetch requests");
  },

  async getRequest(requestId) {
    const response = await fetch(`${API_BASE_URL}${BASE}/${requestId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response, "Failed to fetch request details");
  },

  // fileUrl is the attachment's API path from the request detail; returns a Blob
  async getAttachment(fileUrl) {
    const response = await fetch(`${API_BASE_URL}${fileUrl}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || "Failed to load attachment");
    }
    return response.blob();
  },

  // CertBuddy staff only: the ticket-raiser's recent SigNoz traces, by service.
  async getUserTraces(requestId) {
    const response = await fetch(`${API_BASE_URL}${BASE}/${requestId}/traces`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response, "Failed to fetch user traces");
  },

  async getAllRequests(status = null, category = null, limit = 100) {
    const params = new URLSearchParams();
    if (status) {
      params.append("status", status);
    }
    if (category) {
      params.append("category", category);
    }
    params.append("limit", String(limit));

    const response = await fetch(`${API_BASE_URL}${BASE}/admin/all?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response, "Failed to fetch requests");
  },

  // --- Comments ---

  async addComment(requestId, { body, statusChange = null, attachments = [] }) {
    const formData = new FormData();
    formData.append("body", body);
    if (statusChange) {
      formData.append("status_change", statusChange);
    }
    if (attachments.length > 0) {
      attachments.forEach((file) => {
        formData.append("attachments", file);
      });
    }

    const response = await fetch(`${API_BASE_URL}${BASE}/${requestId}/comments`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: formData,
    });
    return handleResponse(response, "Failed to add comment");
  },

  // --- Status ---

  async updateStatus(requestId, status) {
    const formData = new FormData();
    formData.append("status", status);

    const response = await fetch(`${API_BASE_URL}${BASE}/${requestId}/status`, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: formData,
    });
    return handleResponse(response, "Failed to update status");
  },

  // --- Admin: Priority, Assignee, Release ---

  async updateDetails(requestId, { priority, assignedTo, release }) {
    const formData = new FormData();
    if (priority !== undefined) {
      formData.append("priority", priority || "");
    }
    if (assignedTo !== undefined) {
      formData.append("assigned_to", assignedTo || "");
    }
    if (release !== undefined) {
      formData.append("release", release || "");
    }

    const response = await fetch(`${API_BASE_URL}${BASE}/${requestId}/details`, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: formData,
    });
    return handleResponse(response, "Failed to update details");
  },

  // --- Collaborators ---

  async addCollaborator(requestId, userId, permission = "view") {
    const formData = new FormData();
    formData.append("user_id", userId);
    formData.append("permission", permission);

    const response = await fetch(`${API_BASE_URL}${BASE}/${requestId}/collaborators`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: formData,
    });
    return handleResponse(response, "Failed to add collaborator");
  },

  async removeCollaborator(requestId, collaboratorId) {
    const response = await fetch(
      `${API_BASE_URL}${BASE}/${requestId}/collaborators/${collaboratorId}`,
      {
        method: "DELETE",
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(response, "Failed to remove collaborator");
  },

  // --- Sub-tasks ---

  async addTask(requestId, title) {
    const formData = new FormData();
    formData.append("title", title);

    const response = await fetch(`${API_BASE_URL}${BASE}/${requestId}/tasks`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: formData,
    });
    return handleResponse(response, "Failed to add task");
  },

  async updateTask(requestId, taskId, { completed, title }) {
    const formData = new FormData();
    if (completed !== undefined) {
      formData.append("completed", completed ? "1" : "0");
    }
    if (title !== undefined) {
      formData.append("title", title);
    }

    const response = await fetch(`${API_BASE_URL}${BASE}/${requestId}/tasks/${taskId}`, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: formData,
    });
    return handleResponse(response, "Failed to update task");
  },

  async deleteTask(requestId, taskId) {
    const response = await fetch(`${API_BASE_URL}${BASE}/${requestId}/tasks/${taskId}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });
    return handleResponse(response, "Failed to delete task");
  },

  // --- Org members for pickers ---

  async getOrgMembers(orgId) {
    const response = await fetch(`${API_BASE_URL}/api/orgs/${orgId}/users`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response, "Failed to fetch org members");
  },
};
